import {
  Autocomplete,
  Box,
  BoxProps,
  Skeleton,
  Stack,
  TextField,
} from "@mui/material";
import * as React from "react";
import {
  ReserveOptions,
  ReservesCourseOption,
  ReservesDepartmentOption,
  ReservesInstructorOption,
  ReservesOptionsParameters,
  ReservesOptionsReturn,
} from "./ReservesSearchForm";
import { useBranchLimits } from "./BranchLimits";
import { useLanguage } from "./Language";
import axios from "axios";

type ReservesOption =
  | ReservesCourseOption
  | ReservesDepartmentOption
  | ReservesInstructorOption;

// TODO can we extend AutocompleteProps interface from MUI?
interface ReservesFilterAutocompleteProps {
  label: string;
  loaded: boolean;
  value: string;
  options: ReservesOption[];
  onChange: (value: string | null) => void;
  getOptionLabel: (value: ReservesOption) => string;
}

const ReservesFilterAutocomplete: React.FC<ReservesFilterAutocompleteProps> = ({
  label,
  loaded,
  value,
  onChange,
  options,
  ...props
}) => {
  const { translate } = useLanguage();
  const selectedOption = React.useMemo(() => {
    if (!loaded) {
      return null;
    }
    if (!value) {
      return null;
    }
    if (options) {
      const match = options.find((option) => option.value === value);
      if (!match) {
        return null;
      }
      return match;
    }

    return null;
  }, [options, value, loaded]);

  return (
    <>
      {(loaded || selectedOption) && (
        <Autocomplete
          {...props}
          noOptionsText={translate("noOptions")}
          isOptionEqualToValue={(option, value) => {
            return option.value === value.value;
          }}
          value={selectedOption}
          onChange={(event, newValue: ReservesOption | null, reason) => {
            if (reason === "clear") {
              onChange(null);
            } else {
              onChange(newValue?.value || null);
            }
          }}
          options={options}
          renderInput={(params) => <TextField {...params} label={label} />}
        />
      )}
      {!loaded && !selectedOption && <Skeleton height={"56px"}></Skeleton>}
    </>
  );
};

export interface ReservesFilterControlProps extends BoxProps {
  instructor: ReservesInstructorOption["value"] | null;
  course: ReservesCourseOption["value"] | null;
  department: ReservesDepartmentOption["value"] | null;
  onChangeCourse: (event: ReservesCourseOption["value"] | null) => void;
  onChangeDepartment: (event: ReservesDepartmentOption["value"] | null) => void;
  onChangeInstructor: (event: ReservesInstructorOption["value"] | null) => void;
}

// the parent should control the state of instructor, department, and course
export const ReservesFilterControl: React.FC<ReservesFilterControlProps> = ({
  onChangeCourse,
  onChangeDepartment,
  onChangeInstructor,
  instructor,
  department,
  course,
  ...props
}) => {
  const { limitedBranchIds } = useBranchLimits();
  const { translate } = useLanguage();
  const [options, setOptions] = React.useState<ReserveOptions | null>(null);
  const [allOptions, setAllOptions] = React.useState<ReserveOptions>();
  const [loaded, setLoaded] = React.useState<boolean>(false);

  React.useEffect(() => {
    const reservesOptionsParameters: ReservesOptionsParameters = {
      branchFilters: [],
      targetAudience: "",
      department: "",
      course: "",
      instructor: "",
    };
    axios
      .post<ReservesOptionsReturn>(
        "/reserves/options",
        reservesOptionsParameters
      )
      .then(({ data }) => {
        if (data.success) {
          setAllOptions(data.options);
        } else {
          console.error("ReservesOptionsReturn failure", data);
        }
      })
      .catch((e) => console.error("Error fetching reserve options", e));
  }, []);

  const extendOptionsWithValue = <T extends { value: string }>(
    value: string,
    filteredOptions: T[] = [],
    allOptions: T[] = []
  ): T[] => {
    if (!value || filteredOptions.some((option) => option.value === value)) {
      return filteredOptions;
    }

    const match = allOptions.find((option) => option.value === value);
    return match ? [match, ...filteredOptions] : filteredOptions;
  };

  const useExtendedOptions = <T extends { value: string }>(
    value: string,
    filteredOptions?: T[],
    unfilteredOptions?: T[]
  ) =>
    React.useMemo(() => {
      if (!filteredOptions || !unfilteredOptions) {
        return [];
      }
      return extendOptionsWithValue(value, filteredOptions, unfilteredOptions);
    }, [value, filteredOptions, unfilteredOptions]);

  const instructorOptions = useExtendedOptions(
    instructor,
    options?.instructors,
    allOptions?.instructors
  );

  const courseOptions = useExtendedOptions(
    course,
    options?.courses,
    allOptions?.courses
  );

  const departmentOptions = useExtendedOptions(
    department,
    options?.departments,
    allOptions?.departments
  );

  // when inputs change, we re-request options so that they are filtered to relevant combinations
  React.useEffect(() => {
    setLoaded(false);
    if (limitedBranchIds) {
      const reservesOptionsParameters: ReservesOptionsParameters = {
        branchFilters: [...limitedBranchIds],
        targetAudience: "",
        department: department || "",
        course: course || "",
        instructor: instructor || "",
      };
      axios
        .post<ReservesOptionsReturn>(
          "/reserves/options",
          reservesOptionsParameters
        )
        .then(({ data }) => {
          if (data.success) {
            setOptions(data.options);
            setLoaded(true);
          } else {
            console.error("ReservesOptionsReturn failure", data);
          }
        })
        .catch((e) => console.error("Error fetching reserve options", e));
    }
  }, [instructor, course, department, limitedBranchIds]);

  return (
    <Box {...props}>
      <Stack gap={2} maxWidth={"320px"}>
        <ReservesFilterAutocomplete
          label={translate("departments")}
          loaded={loaded}
          value={department}
          options={departmentOptions}
          onChange={(value) => onChangeDepartment(value)}
          getOptionLabel={(option: ReservesDepartmentOption) =>
            `${option.name} - ${option.comment}`
          }
        ></ReservesFilterAutocomplete>
        <ReservesFilterAutocomplete
          label={translate("courses")}
          loaded={loaded}
          value={course}
          options={courseOptions}
          onChange={(value) => onChangeCourse(value)}
          getOptionLabel={(option: ReservesCourseOption) =>
            `${option.name} - ${option.comment}`
          }
        ></ReservesFilterAutocomplete>
        <ReservesFilterAutocomplete
          label={translate("instructors")}
          loaded={loaded}
          value={instructor}
          options={instructorOptions}
          onChange={(value) => onChangeInstructor(value)}
          getOptionLabel={(option: ReservesInstructorOption) =>
            `${option.name}`
          }
        ></ReservesFilterAutocomplete>
      </Stack>
    </Box>
  );
};
